import React, { useState, useEffect } from "react";
import { getAuthHeader } from "../../api/authToken.js";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import "../../styles/App.css";
import GroupLabel from "../../components/GroupLabel.js";

const StudentTransferTable = () => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [dormitories, setDormitories] = useState([]);
  const [selectedDormitory, setSelectedDormitory] = useState("");
  const [transferStates, setTransferStates] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const API = process.env.REACT_APP_API_BASE;

  const [filters, setFilters] = useState({
    pib: "",
    group: "",
    room_number: "",
  });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const handleTransferChange = (studentId, field, value) => {
    setTransferStates((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value,
      },
    }));
  };

  const [rehousingOptions, setRehousingOptions] = useState({}); // Стан для збереження вибору
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const response = await fetch(`${API}/api/student-relocation`, {
          headers: { Authorization: getAuthHeader() },
        });
        const data = await response.json();
        setStudents(data);
        setFilteredStudents(data);
        const initialTransferStates = data.reduce((acc, student) => {
          acc[student.id] = { transferToOther: false, selectedRoom: "" };
          return acc;
        }, {});
        setTransferStates(initialTransferStates);
      } catch (error) {
        console.error("Помилка завантаження студентів:", error);
      }
    };

    fetchStudents();
  }, []);

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const response = await fetch(`${API}/api/available-rooms`, {
          headers: { Authorization: getAuthHeader() },
        });
        const data = await response.json();
        setRooms(data);
      } catch (error) {
        console.error("Помилка завантаження кімнат:", error);
      }
    };

    fetchRooms();
  }, []);

  useEffect(() => {
    const fetchDormitories = async () => {
      try {
        const response = await fetch(`${API}/api/dormitories`, {
          headers: { Authorization: getAuthHeader() },
        });
        const data = await response.json();
        setDormitories(data);
      } catch (error) {
        console.error("Помилка завантаження гуртожитків:", error);
      }
    };

    fetchDormitories();
  }, []);

  useEffect(() => {
    let filtered = students;

    if (selectedDormitory) {
      fetch(
        `${API}/api/students-by-dormitory?dormitory_id=${selectedDormitory}`,
        {
          headers: { Authorization: getAuthHeader() },
        }
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error("Помилка завантаження студентів");
          }
          return response.json();
        })
        .then((data) => setStudents(data))
        .catch((error) => {
          console.error("Помилка:", error);
          setStudents([]);
        });
    }

    if (filters.pib) {
      filtered = filtered.filter((student) =>
        student.pib.toLowerCase().includes(filters.pib.toLowerCase())
      );
    }

    if (filters.group) {
      filtered = filtered.filter((student) =>
        (student.group || "").toLowerCase().includes(filters.group.toLowerCase())
      );
    }

    if (filters.room_number) {
      filtered = filtered.filter((student) =>
        student.room_number
          ?.toString()
          .toLowerCase()
          .includes(filters.room_number.toLowerCase())
      );
    }

    setFilteredStudents(filtered);
  }, [filters, selectedDormitory, students]);

  const handleTransfer = async (studentId, keepSameRoom = false) => {
    const student = students.find((s) => s.id === studentId);
    if (!student) {
      console.error(`Студент з ID ${studentId} не знайдений.`);
      return;
    }

    const newRoomId = keepSameRoom
      ? student.room_id 
      : transferStates[studentId]?.selectedRoom || null;

    if (!newRoomId) {
      alert("Помилка: ID кімнати не може бути порожнім!");
      return;
    }

    try {
      const response = await fetch(`${API}/api/relocate-student/${studentId}`, {
        method: "POST",
        headers: {
          Authorization: getAuthHeader(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ newRoomId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP помилка! Статус: ${response.status}`);
      }

      const data = await response.json();
      alert(data.message || `Студент ${studentId} успішно переселений.`);

      // Оновлюємо список студентів
      setStudents((prev) =>
        prev.map((s) => (s.id === studentId ? { ...s, room_id: newRoomId } : s))
      );
      window.location.reload();
    } catch (error) {
      console.error(`Помилка переселення студента ${studentId}:`, error);
      setError("Помилка сервера. Спробуйте пізніше.");
    }
  };

  const handleResetRelocation = async () => {
    const confirmation = window.confirm(
      "Ви впевнені, що хочете скинути переселення для всіх студентів?"
    );
    if (!confirmation) return;

    try {
      const response = await fetch(`${API}/api/reset-relocation`, {
        method: "POST",
        headers: {
          Authorization: getAuthHeader(),
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP помилка! Статус: ${response.status}`);
      }

      alert("Переселення для всіх студентів успішно скинуто!");

      // Оновлюємо список студентів
      setStudents((prev) => prev.map((s) => ({ ...s, needs_relocation: 0 })));
      window.location.reload();
    } catch (error) {
      console.error("Помилка скидання переселення:", error);
      setError("Помилка сервера. Спробуйте пізніше.");
    }
  };

  // Обробка виселення студента
  const handleExpulsion = (student) => {
    const canBeRehoused = rehousingOptions[student.id] ?? true;
    const confirmation = window.confirm(
      `Ви впевнені, що хочете виселити ${student.pib} з ${
        student.violations
      } порушеннями? 
       Можливість повторного поселення: ${canBeRehoused ? "Так" : "Ні"}
       УВАГА! Всі пов'язані дані з цим студентом теж будуть видалені!`
    );

    if (confirmation) {
      fetch(`${API}/api/expel-student/${student.id}`, {
        method: "POST",
        headers: {
          Authorization: getAuthHeader(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ canBeRehoused }),
      })
        .then((res) => res.json())
        .then(() => {
          alert("Студента успішно виселено!");
          setStudents(students.filter((s) => s.id !== student.id));
        })
        .catch((err) => console.error(err));
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    const sortedStudents = [...filteredStudents].sort((a, b) => {
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setFilteredStudents(sortedStudents);
  };

  // Обробка кліку на рядок
  const handleRowClick = (studentId) => {
    navigate(`/student/${studentId}`); // Перехід на сторінку студента
  };

  return (
    <div className="page-container">
      <div className="header-content">
        <Link to="/transfer" className="propese-button">
          {" "}
          Пересилити одного
        </Link>
        <br />
        <a href="#" onClick={handleResetRelocation} className="propese-button">
          Скинути переселення для всіх
        </a>
      </div>

      {message && <p style={{ color: "green" }}>{message}</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}

      <table className="table">
        <thead>
          <tr>
            <th className="table-header" onClick={() => handleSort("pib")}>
              Ім'я{" "}
              {sortConfig.key === "pib"
                ? sortConfig.direction === "asc"
                  ? "↑"
                  : "↓"
                : "↑↓"}
            </th>
            <th className="table-header" onClick={() => handleSort("group")}>
              Група{" "}
              {sortConfig.key === "group"
                ? sortConfig.direction === "asc"
                  ? "↑"
                  : "↓"
                : "↑↓"}
            </th>
            <th
              className="table-header"
              onClick={() => handleSort("room_number")}
            >
              Поточна кімната{" "}
              {sortConfig.key === "room_number"
                ? sortConfig.direction === "asc"
                  ? "↑"
                  : "↓"
                : "↑↓"}
            </th>
            <th className="table-header">В іншу кімнату</th>
            <th className="table-header">Оберіть кімнату</th>
            <th className="table-header" colSpan={3}>
              Дії
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredStudents.map((student) => (
            <tr key={student.id}>
              <td
                className="table-content"
                onClick={() => handleRowClick(student.id)}
                style={{ cursor: "pointer" }}
              >
                {student.pib}
              </td>
              <td className="table-content"><GroupLabel group={student.group} /></td>
              <td className="table-content">
                {student.room_number || "Немає кімнати"}
              </td>
              <td className="table-content">
                <input
                  type="checkbox"
                  checked={transferStates[student.id]?.transferToOther || false}
                  onChange={(e) =>
                    handleTransferChange(
                      student.id,
                      "transferToOther",
                      e.target.checked
                    )
                  }
                />
              </td>
              <td className="table-content">
                <select
                  disabled={!transferStates[student.id]?.transferToOther}
                  value={transferStates[student.id]?.selectedRoom || ""}
                  onChange={(e) =>
                    handleTransferChange(
                      student.id,
                      "selectedRoom",
                      e.target.value
                    )
                  }
                >
                  <option value="">Оберіть кімнату</option>
                  {[...rooms]
                    .sort((a, b) =>
                      `${a.dormitory_name || ""} ${
                        a.room_number
                      }`.localeCompare(
                        `${b.dormitory_name || ""} ${b.room_number}`,
                        "uk"
                      )
                    )
                    .map((room) => (
                      <option key={room.id} value={room.id}>
                        {`${room.dormitory_name} - Кімната ${room.room_number} (вільних ліжок: ${room.free_beds})`}
                      </option>
                    ))}
                </select>
              </td>
              <td className="table-content">
                <button
                  className="restore-button"
                  onClick={() => handleTransfer(student.id)}
                >
                  Переселити
                </button>
              </td>
              <td className="table-content">
                <button
                  className="restore-button"
                  onClick={() => handleTransfer(student.id, true)}
                >
                  Переселити в ту ж кімнату
                </button>
              </td>
              <td className="table-content">
                <button
                  className="remove-button"
                  onClick={() => handleExpulsion(student)}
                >
                  Виселити
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default StudentTransferTable;
